import React from 'react';

/**
 * 编辑器初始化回调工具函数
 * 用于处理编辑器完全初始化后的回调逻辑，包括重试机制和状态检查
 */

/**
 * 检查编辑器是否完全准备好（DOM 已渲染）
 * @param editor - TinyMCE 编辑器实例
 * @returns 是否准备好
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function checkEditorReady(editor: any): boolean {
  try {
    const body = editor?.getBody();
    // 如果能够获取到 body 且 body 有 ownerDocument，说明编辑器 DOM 已经准备好
    return !!(body && body.ownerDocument);
  } catch (e) {
    console.warn('Editor body not ready yet', e);
    return false;
  }
}

/**
 * 带重试的执行函数
 * @param fn - 要执行的函数，返回 true 表示成功，false 表示需要重试
 * @param options - 配置选项
 */
export function executeWithRetry(
  fn: () => boolean,
  options: {
    maxRetries?: number;
    retryDelay?: number;
    onRetry?: (retryCount: number, maxRetries: number) => void;
    onSuccess?: () => void;
    onFailure?: () => void;
  } = {}
): void {
  const { maxRetries = 20, retryDelay = 50, onRetry, onSuccess, onFailure } = options;
  let retryCount = 0;

  const tryExecute = () => {
    const success = fn();
    if (success) {
      onSuccess?.();
      return;
    }

    retryCount++;
    if (retryCount < maxRetries) {
      onRetry?.(retryCount, maxRetries);
      setTimeout(tryExecute, retryDelay);
    } else {
      onFailure?.();
    }
  };

  tryExecute();
}

/**
 * 创建编辑器初始化回调函数
 * 用于在编辑器完全初始化后安全地调用 onEditCreated 回调
 * 
 * @param options - 配置选项
 * @param options.editorRef - 编辑器实例的 ref
 * @param options.componentRefInstance - 组件 ref 实例（通过 useImperativeHandle 创建）
 * @param options.onEditCreated - 编辑器创建完成后的回调函数
 * @param options.maxRetries - 最大重试次数，默认 3
 * @param options.retryDelay - 重试延迟（毫秒），默认 50
 * @returns 编辑器初始化回调函数
 */
export function createEditorInitCallback<T = unknown>(options: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editorRef: React.MutableRefObject<any>;
  componentRefInstance: React.MutableRefObject<T | null>;
  onEditCreated?: (ref: T) => void;
  maxRetries?: number;
  retryDelay?: number;
}): (editor: unknown) => void {
  const {
    editorRef,
    componentRefInstance,
    onEditCreated,
    maxRetries = 3,
    retryDelay = 50,
  } = options;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (editor: any) => {
    console.log('init_instance_callback', editor);
    // 确保编辑器实例已设置
    editorRef.current = editor;

    // 在编辑器完全初始化后调用 onEditCreated
    if (onEditCreated) {
      // 首先确保 useImperativeHandle 已经完成初始化
      requestAnimationFrame(() => {
        executeWithRetry(
          () => {
            // 检查 componentRefInstance 和编辑器是否都准备好
            if (componentRefInstance.current && checkEditorReady(editor)) {
              console.log('Editor ready, calling onEditCreated');
              onEditCreated(componentRefInstance.current);
              return true;
            }
            return false;
          },
          {
            maxRetries,
            retryDelay,
            onRetry: (retryCount, maxRetries) => {
              console.log(`Editor not ready, retrying onEditCreated... (${retryCount}/${maxRetries})`);
            },
            onFailure: () => {
              // 最后一次尝试，即使检查失败也调用（避免无限等待）
              if (componentRefInstance.current) {
                console.log('Calling onEditCreated as fallback');
                onEditCreated(componentRefInstance.current);
              }
            },
          }
        );
      });
    }
  };
}

